import { httpClient } from "./client";
import { InvalidApiResponseError } from "./errors";
import { isPartyEligibilityResponse } from "./queue";
import type { DropInCheckInRequest, DropInPaymentOption, UUID } from "../types";
import type {
  CheckedInPlayerResponse,
  CourtAssignmentResponse,
  CourtBoardEntry,
  CourtBoardLineGroup,
  CourtBoardPlayingGroup,
  CourtBoardPlayingGroupOperatorAssignment,
  CourtBoardPresence,
  CourtBoardResponse,
  DropInActiveSessionPage,
  DropInCancellationDecisionResponse,
  DropInCancellationRequest,
  DropInCancellationResponse,
  DropInSessionResponse,
  FacilityWaiverDocumentResponse,
  FacilityWaiverStatusResponse,
  OpenPlayCancellationPolicyConfigurationResponse,
  OpenPlayCancellationPolicyCreate,
  OpenPlayCancellationPolicyPreviewResponse,
  OpenPlayCancellationPolicyResponse,
  QueueOperatorActionRequest,
  QueueEntryResponse,
  StaffCheckInRequest,
  WaiverAcceptanceResponse,
} from "../types/generated";

export interface ListActiveSessionsOptions {
  q?: string;
  limit?: number;
  cursor?: string;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const COURT_OPERATIONAL_STATES = new Set([
  "operational",
  "maintenance",
  "closed",
]);
const COURT_OCCUPANCY_STATES = new Set([
  "available",
  "waiting",
  "in_play",
  "reserved",
]);
const COURT_AVAILABILITY_STATES = new Set([
  "assignable",
  "joinable",
  "waitlist_only",
  "unavailable",
]);
const PARTY_COURT_REASONS = new Set([
  "eligible",
  "party_blocked",
  "queue_closed",
  "court_maintenance",
  "court_closed",
  "court_reserved",
  "party_too_large",
  "facility_closing",
  "game_end_unknown",
  "member_session_too_short",
]);
const COURT_ASSIGNMENT_TYPES = new Set(["fresh", "top_off"]);
const COURT_ASSIGNMENT_POLICY_REASONS = new Set([
  "fresh_immediate_ready",
  "fresh_target_reached",
  "fresh_max_wait_reached",
  "fresh_exclusive_party",
  "filled_existing_court",
  "extended_existing_court",
]);
const NON_PUBLIC_COURT_BOARD_ID_KEYS = [
  "user_id",
  "member_id",
  "session_id",
  "entry_id",
  "queue_entry_id",
  "assignment_id",
  "party_id",
  "group_id",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isUuid(value: unknown): value is UUID {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 1;
}

function isNullableString(value: unknown): boolean {
  return value == null || typeof value === "string";
}

function isIsoInstant(value: unknown): value is string {
  return (
    typeof value === "string" &&
    ISO_DATE_TIME_PATTERN.test(value) &&
    Number.isFinite(Date.parse(value))
  );
}

function isNullableInstant(value: unknown): boolean {
  return value == null || isIsoInstant(value);
}

function containsNonPublicCourtBoardId(
  value: Record<string, unknown>,
): boolean {
  return NON_PUBLIC_COURT_BOARD_ID_KEYS.some((key) => key in value);
}

function isCourtBoardPresence(value: unknown): boolean {
  return (
    isRecord(value) &&
    !containsNonPublicCourtBoardId(value) &&
    typeof value.presence_id === "string" &&
    value.presence_id.length > 0 &&
    isNullableString(value.display_name) &&
    isNullableString(value.avatar_url) &&
    (value.is_current_user == null ||
      typeof value.is_current_user === "boolean")
  );
}

function isCourtBoardLineGroup(value: unknown): value is CourtBoardLineGroup {
  return (
    isRecord(value) &&
    !containsNonPublicCourtBoardId(value) &&
    typeof value.line_group_key === "string" &&
    value.line_group_key.length > 0 &&
    isPositiveInteger(value.position) &&
    isIsoInstant(value.joined_at) &&
    (value.estimated_wait_minutes == null ||
      isNonNegativeInteger(value.estimated_wait_minutes)) &&
    isPositiveInteger(value.player_count) &&
    Array.isArray(value.members) &&
    value.members.every(isCourtBoardPresence) &&
    value.members.length === value.player_count &&
    typeof value.is_current_group === "boolean"
  );
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: ReadonlySet<string>,
): boolean {
  return Object.keys(value).every((key) => allowedKeys.has(key));
}

const PLAYING_GROUP_OPERATOR_ASSIGNMENT_KEYS = new Set([
  "assignment_id",
  "expires_at",
  "assignment_type",
  "policy_reason",
]);

function isCourtBoardPlayingGroupOperatorAssignment(
  value: unknown,
): value is CourtBoardPlayingGroupOperatorAssignment {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, PLAYING_GROUP_OPERATOR_ASSIGNMENT_KEYS) &&
    isUuid(value.assignment_id) &&
    isIsoInstant(value.expires_at) &&
    (value.assignment_type == null ||
      (typeof value.assignment_type === "string" &&
        COURT_ASSIGNMENT_TYPES.has(value.assignment_type))) &&
    (value.policy_reason == null ||
      (typeof value.policy_reason === "string" &&
        COURT_ASSIGNMENT_POLICY_REASONS.has(value.policy_reason)))
  );
}

const PLAYING_GROUP_KEYS = new Set([
  "playing_group_key",
  "player_count",
  "members",
  "operator_assignment",
]);

function isCourtBoardPlayingGroup(
  value: unknown,
): value is CourtBoardPlayingGroup {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, PLAYING_GROUP_KEYS) &&
    typeof value.playing_group_key === "string" &&
    value.playing_group_key.length > 0 &&
    isPositiveInteger(value.player_count) &&
    Array.isArray(value.members) &&
    value.members.every(isCourtBoardPresence) &&
    value.members.length === value.player_count &&
    (value.operator_assignment == null ||
      isCourtBoardPlayingGroupOperatorAssignment(value.operator_assignment))
  );
}

function isSameCourtBoardPresence(
  left: CourtBoardPresence,
  right: CourtBoardPresence,
): boolean {
  return (
    left.presence_id === right.presence_id &&
    left.display_name === right.display_name &&
    left.avatar_url === right.avatar_url &&
    left.is_current_user === right.is_current_user
  );
}

function isPartyCourtPlacement(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.can_place === "boolean" &&
    typeof value.reason === "string" &&
    PARTY_COURT_REASONS.has(value.reason) &&
    Array.isArray(value.blocking_member_ids) &&
    value.blocking_member_ids.every(isUuid) &&
    isNullableInstant(value.required_until)
  );
}

function isCourtBoardEntry(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isUuid(value.court_id) ||
    typeof value.name !== "string" ||
    !isPositiveInteger(value.capacity) ||
    typeof value.operational_state !== "string" ||
    !COURT_OPERATIONAL_STATES.has(value.operational_state) ||
    typeof value.occupancy_state !== "string" ||
    !COURT_OCCUPANCY_STATES.has(value.occupancy_state) ||
    typeof value.availability_state !== "string" ||
    !COURT_AVAILABILITY_STATES.has(value.availability_state) ||
    typeof value.can_join_line !== "boolean" ||
    (value.party_placement != null &&
      !isPartyCourtPlacement(value.party_placement)) ||
    !Array.isArray(value.players) ||
    !value.players.every(isCourtBoardPresence) ||
    !Array.isArray(value.playing_groups) ||
    !value.playing_groups.every(isCourtBoardPlayingGroup) ||
    !isNonNegativeInteger(value.available_slots) ||
    typeof value.is_private !== "boolean" ||
    !isNonNegativeInteger(value.line_group_count) ||
    !isNonNegativeInteger(value.line_player_count) ||
    !Array.isArray(value.line_groups) ||
    !value.line_groups.every(isCourtBoardLineGroup) ||
    (value.next_up_group != null && !isCourtBoardLineGroup(value.next_up_group))
  ) {
    return false;
  }

  const lineGroups = value.line_groups as CourtBoardLineGroup[];
  const players = value.players as CourtBoardPresence[];
  const playingGroups = value.playing_groups as CourtBoardPlayingGroup[];
  const lineGroupKeys = lineGroups.map((group) => group.line_group_key);
  const playingGroupKeys = playingGroups.map(
    (group) => group.playing_group_key,
  );
  const operatorAssignmentIds = playingGroups.flatMap((group) =>
    group.operator_assignment ? [group.operator_assignment.assignment_id] : [],
  );
  const groupedPlayers = playingGroups.flatMap((group) => group.members);
  const visiblePlayerCount = lineGroups.reduce(
    (total, group) => total + group.player_count,
    0,
  );
  const playerPresenceIds = players.map((player) => player.presence_id);
  const groupedPresenceIds = groupedPlayers.map((player) => player.presence_id);
  const hasLiveGame = value.occupancy_state === "in_play";
  const groupedRosterIsCoherent = hasLiveGame
    ? playingGroups.length > 0 &&
      groupedPlayers.length === players.length &&
      groupedPlayers.every((player, index) =>
        isSameCourtBoardPresence(player, players[index]),
      )
    : playingGroups.length === 0;
  const capacityIsCoherent = hasLiveGame
    ? players.length > 0 &&
      players.length <= value.capacity &&
      value.available_slots === value.capacity - players.length
    : players.length === 0 &&
      value.available_slots === 0 &&
      value.is_private === false;
  return (
    new Set(lineGroupKeys).size === lineGroupKeys.length &&
    new Set(playingGroupKeys).size === playingGroupKeys.length &&
    new Set(operatorAssignmentIds).size === operatorAssignmentIds.length &&
    new Set(playerPresenceIds).size === playerPresenceIds.length &&
    new Set(groupedPresenceIds).size === groupedPresenceIds.length &&
    groupedRosterIsCoherent &&
    capacityIsCoherent &&
    value.line_group_count >= lineGroups.length &&
    value.line_player_count >= visiblePlayerCount
  );
}

function isCourtBoardResponse(
  value: unknown,
  expectedFacilityId: UUID,
): value is CourtBoardResponse {
  if (
    !isRecord(value) ||
    !isNonNegativeInteger(value.revision) ||
    typeof value.queue_open !== "boolean" ||
    (value.active_now != null && typeof value.active_now !== "boolean") ||
    !Array.isArray(value.courts) ||
    !value.courts.every(isCourtBoardEntry) ||
    !Array.isArray(value.looking_to_play) ||
    !value.looking_to_play.every(isCourtBoardPresence)
  ) {
    return false;
  }
  const courts = value.courts as CourtBoardEntry[];
  const lookingToPlay = value.looking_to_play as CourtBoardPresence[];
  const courtIds = courts.map((court) => court.court_id);
  if (new Set(courtIds).size !== courtIds.length) return false;
  const operatorAssignmentIds = courts.flatMap((court) =>
    (court.playing_groups ?? []).flatMap((group) =>
      group.operator_assignment
        ? [group.operator_assignment.assignment_id]
        : [],
    ),
  );
  if (new Set(operatorAssignmentIds).size !== operatorAssignmentIds.length) {
    return false;
  }

  const physicalPresences = [
    ...courts.flatMap((court) => court.players ?? []),
    ...lookingToPlay,
  ];
  const physicalPresenceIds = physicalPresences.map(
    (presence) => presence.presence_id,
  );
  if (
    new Set(physicalPresenceIds).size !== physicalPresenceIds.length ||
    physicalPresences.filter((presence) => presence.is_current_user === true)
      .length > 1
  ) {
    return false;
  }

  if (value.party_eligibility == null) return true;
  if (
    !isRecord(value.party_eligibility) ||
    !isUuid(value.party_eligibility.party_id) ||
    value.party_eligibility.revision !== value.revision
  ) {
    return false;
  }
  return isPartyEligibilityResponse(
    value.party_eligibility,
    expectedFacilityId,
    value.party_eligibility.party_id,
  );
}

export const dropinApi = {
  checkIn: (facilityId: string, data: DropInCheckInRequest) =>
    httpClient.post<DropInSessionResponse>(
      `/facilities/${facilityId}/drop-in/check-in`,
      data,
    ),

  getSession: (facilityId: string) =>
    httpClient.get<DropInSessionResponse | null>(
      `/facilities/${facilityId}/drop-in/session`,
    ),

  listCurrentSessions: () =>
    httpClient.get<DropInSessionResponse[]>("/drop-in/sessions/mine"),

  getPaymentOptions: (facilityId: string) =>
    httpClient.get<DropInPaymentOption>(
      `/facilities/${facilityId}/drop-in/payment-options`,
    ),

  getCancellationPolicy: (facilityId: string) =>
    httpClient.get<OpenPlayCancellationPolicyConfigurationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/cancellation-policy`,
    ),

  previewCancellationPolicy: (
    facilityId: string,
    data: OpenPlayCancellationPolicyCreate,
  ) =>
    httpClient.post<OpenPlayCancellationPolicyPreviewResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/cancellation-policy/preview`,
      data,
    ),

  publishCancellationPolicy: (
    facilityId: string,
    data: OpenPlayCancellationPolicyCreate,
  ) =>
    httpClient.put<OpenPlayCancellationPolicyResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/cancellation-policy`,
      data,
    ),

  getCancellationDecision: (facilityId: string, sessionId: string) =>
    httpClient.get<DropInCancellationDecisionResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/sessions/${encodeURIComponent(sessionId)}/cancellation`,
    ),

  cancelSession: (
    facilityId: string,
    sessionId: string,
    data: DropInCancellationRequest,
  ) =>
    httpClient.post<DropInCancellationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/sessions/${encodeURIComponent(sessionId)}/cancel`,
      data,
    ),

  recoverCancellation: (facilityId: string, sessionId: string) =>
    httpClient.post<DropInCancellationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/sessions/${encodeURIComponent(sessionId)}/cancellation/recover`,
    ),

  listActiveSessions: (
    facilityId: string,
    options: ListActiveSessionsOptions = {},
  ) => {
    const params = new URLSearchParams();
    params.set("limit", String(options.limit ?? 50));
    if (options.q) params.set("q", options.q);
    if (options.cursor) params.set("cursor", options.cursor);
    return httpClient.get<DropInActiveSessionPage>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/sessions?${params.toString()}`,
    );
  },

  endAdmission: (
    facilityId: string,
    sessionId: string,
    body: QueueOperatorActionRequest,
  ) =>
    httpClient.post<DropInSessionResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/drop-in/sessions/${encodeURIComponent(sessionId)}/end`,
      body,
    ),

  staffCheckIn: (facilityId: string, data: StaffCheckInRequest) =>
    httpClient.post<DropInSessionResponse>(
      `/facilities/${facilityId}/drop-in/staff-check-in`,
      data,
    ),

  // ---- Per-person, non-delegable facility waiver (consent module) ----------
  // The waiver lives in the consent module and is surfaced under
  // /facilities/{id}/waiver + /users/me/facilities/{id}/waiver/*. The drop-in
  // module does not own it; the frontend composes the gate around check-in
  // (DropInService.check_in() has no server-side waiver enforcement today —
  // tracked as a backend parity gap). Each player accepts their OWN waiver.

  /** Has THIS user accepted the facility's current waiver? `current_version_id`
   * is null when the facility has published no waiver (gate is a no-op). */
  getWaiverStatus: (facilityId: string) =>
    httpClient.get<FacilityWaiverStatusResponse>(
      `/users/me/facilities/${facilityId}/waiver/status`,
    ),

  /** Current legal document with authoritative facility identity. Null when none. */
  getCurrentWaiver: (facilityId: string) =>
    httpClient.get<FacilityWaiverDocumentResponse | null>(
      `/facilities/${facilityId}/waiver`,
    ),

  /** Accept the current waiver for THIS user only. `versionId` MUST be the
   * `current_version_id` from getWaiverStatus/getCurrentWaiver. */
  acceptWaiver: (facilityId: string, versionId: string) =>
    httpClient.post<WaiverAcceptanceResponse>(
      `/users/me/facilities/${facilityId}/waiver/accept`,
      { version_id: versionId },
    ),

  /** Players with an ACTIVE drop-in session at `facilityId` — gated to a caller
   *  who is THEMSELVES checked in. The backend privacy-filters to public profiles
   *  only. Returns the roster in last_active_at DESC order (most-recently-active
   *  first); the FE must NOT re-sort (slice 3b party-invite picker). */
  listCheckedInPlayers: (facilityId: string) =>
    httpClient.get<CheckedInPlayerResponse[]>(
      `/facilities/${facilityId}/drop-in/checked-in-players`,
    ),

  // ---- Self-serve court board (Phase 2 P2.S2) ----------------------------------

  /** Current court-board state for `facilityId`.
   *  Returns courts with their live state (open/in_game/full/private) + players,
   *  and the looking_to_play list (checked-in players not on a court). */
  getCourtBoard: async (facilityId: string) => {
    const endpoint = `/facilities/${facilityId}/drop-in/court-board`;
    const response = await httpClient.get<unknown>(endpoint);
    if (!isUuid(facilityId) || !isCourtBoardResponse(response, facilityId)) {
      throw new InvalidApiResponseError(endpoint);
    }
    return response;
  },

  /** Join a court (solo start → 409 SoloCannotStartCourtError if no partner).
   *  Returns the new CourtAssignmentResponse, or null if the server chose not
   *  to return one (backend spec: 200 + null body is valid). */
  joinCourt: (facilityId: string, courtId: string) =>
    httpClient.post<CourtAssignmentResponse | null>(
      `/facilities/${facilityId}/drop-in/courts/${courtId}/join`,
      {},
    ),

  /** Leave the current court (the caller must be a member of that court). */
  leaveCourt: (facilityId: string, courtId: string) =>
    httpClient.post<void>(
      `/facilities/${facilityId}/drop-in/courts/${courtId}/leave`,
      {},
    ),

  /** Join a court's LINE (self-serve court LINES, ADR-025 Phase D, #1741) — a
   *  court-scoped waiting entry for a court the caller can't immediately claim
   *  (full, or in_game with no open slots). Returns the new QueueEntryResponse.
   *  The backend enforces "one spot anywhere": a caller who already holds an
   *  active entry/line/assignment anywhere at the facility gets a structured
   *  409 (AlreadyInQueueError, code ALREADY_IN_QUEUE). Leaving a line reuses
   *  the existing DELETE /queue/{entry_id} self-leave path (queueApi.leave) —
   *  no separate leaveLine client (clean migration, no dual path). */
  joinLine: (facilityId: string, courtId: string) =>
    httpClient.post<QueueEntryResponse>(
      `/facilities/${facilityId}/drop-in/courts/${courtId}/line`,
      {},
    ),
};
